import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How long do dental implants last?",
    a: "With proper care, dental implants can last a lifetime. However, the crown or restoration may need to be replaced after 10-15 years.",
  },
  {
    q: "Are dental implants painful?",
    a: "The procedure is usually done under local anesthesia, so there should be minimal pain during the surgery. Any discomfort afterward can typically be managed with pain medications.",
  },
  {
    q: "Am I a candidate for dental implants?",
    a: "Most people who are in good overall health and have sufficient bone density in their jaw are candidates for dental implants. A consultation with your dentist will help determine if you’re a good candidate.",
  },
  {
    q: "How long does the implant procedure take?",
    a: "The entire process may take several months, as it involves multiple stages, including surgery, healing, and the placement of the final restoration.",
  },
  {
    q: "How much do dental implants cost?",
    a: "The cost of dental implants varies depending on the number of implants needed, the type of implant, and other factors. It’s important to discuss costs with your dentist beforehand.",
  },
  {
    q: "Can dental implants be rejected?",
    a: "It is rare, but in some cases, implants may fail to integrate with the jawbone, especially if infection occurs. This can be addressed with further treatment.",
  },
  {
    q: "Will my dental implants look natural?",
    a: "Yes, dental implants are designed to look and function like natural teeth, providing a seamless appearance.",
  },
  {
    q: "Are there any risks involved with dental implants?",
    a: "As with any surgery, there are risks such as infection, nerve damage, or implant failure. However, these risks are low and can be minimized with proper care and skilled professionals.",
  },
  {
    q: "Do I need to change my diet after getting implants??",
    a: "For the first few days, you should stick to soft foods. Once healed, you can eat most foods, but it’s important to avoid hard or sticky items that could damage the implant.",
  },
  {
    q: "Can dental implants be used for a full set of teeth?",
    a: "Yes, dental implants can be used to replace an entire set of teeth with techniques like the All-on-4 system, which supports a full arch of replacement teeth.",
  },
];

const PAGE_PATH = "/dental-implants";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const DentalImplants = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Dental Implants in Jaipur | Permanent Tooth Replacement"
        description="Replace missing teeth permanently with dental implants in Vaishali Nagar, Jaipur. Safe, durable and natural-looking results at Qualiteeth Dental Care."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Dental Implants", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Dental Implants in Jaipur | Permanent Tooth Replacement",
            description: "Replace missing teeth permanently with dental implants in Vaishali Nagar, Jaipur. Safe, durable and natural-looking results at Qualiteeth Dental Care.",
            procedureName: "Dental Implant",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Dental Implant",
            description: "Replace missing teeth permanently with dental implants in Vaishali Nagar, Jaipur. Safe, durable and natural-looking results at Qualiteeth Dental Care.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Everything You Need to Know About Dental Implants
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          A dental implant is a titanium screw placed into the jawbone to replace the root of a missing tooth. Once the bone fuses to the implant, a crown is fixed on top. Implants are the closest replacement to a natural tooth because they restore chewing force and help prevent the jawbone loss that follows tooth loss.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Are Dental Implants?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental implants are a modern and reliable solution for replacing
              missing teeth. They are artificial tooth roots, typically made of
              titanium, that are surgically placed into the jawbone. Once the
              implants have fused with the bone through a process called
              osseointegration, they provide a sturdy foundation for placing
              replacement teeth or dental bridges. Dental implants look, feel,
              and function like natural teeth, making them a popular choice for
              people who need tooth replacements.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Dental Implants
            </Heading>
            <Text mt={2} fontWeight="light">
              There are several types of dental implants, each designed for
              different needs:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Endosteal Implants:</strong> These are the most common
                  type of dental implants. They are placed directly into the
                  jawbone and are typically made of titanium. Once the bone
                  heals, a crown is placed on top of the implant.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Subperiosteal Implants:</strong> These implants are
                  placed under the gum but on top of the jawbone, usually when
                  there isn’t enough healthy bone tissue to support endosteal
                  implants. A metal frame is placed beneath the gum, and as the
                  gums heal, the frame becomes fixed in place.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Zygomatic Implants:</strong> These are less commonly
                  used and are placed in the cheekbone (zygoma) instead of the
                  upper jaw. Zygomatic implants are often used in cases where
                  there is insufficient jawbone for endosteal implants.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>All-on-4 Implants:</strong> This is a technique where
                  four implants are placed in the jaw to support a full arch of
                  teeth. It’s ideal for people who need to replace an entire row
                  of teeth but don't have enough bone for individual implants.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Why Dental Implants?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental implants offer several compelling reasons to consider them
              over other options:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Durability:</strong> Implants are designed to last for
                  many years, often even a lifetime with proper care.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Natural Look and Feel:</strong> Dental implants mimic
                  the look and feel of natural teeth. Once placed, they function
                  just like your own teeth, allowing you to eat, speak, and
                  smile without discomfort.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Bone Preservation:</strong> When a tooth is lost, the
                  jawbone can deteriorate over time. Implants help preserve the
                  jawbone and prevent bone loss by stimulating the bone like
                  natural tooth roots.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Enhanced Comfort:</strong> Unlike dentures, dental
                  implants are permanently fixed in place, eliminating the need
                  for adhesives and the discomfort of removable devices.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Improved Self-Esteem:</strong> Dental implants help
                  restore your smile, boosting your confidence and improving
                  your quality of life.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Implants vs Dentures
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental implants and dentures are both common solutions for
              replacing missing teeth, but there are significant differences:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Stability:</strong> Dental implants are fixed in place
                and function like natural teeth, whereas dentures are removable
                and may shift or slip during eating or speaking.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Comfort:</strong> Implants are permanent and
                comfortable, while dentures may cause irritation or require
                adhesives to stay in place.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Maintenance:</strong> Dental implants require the same
                care as natural teeth, such as brushing and flossing. Dentures,
                on the other hand, need to be removed, cleaned, and maintained
                regularly.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bone Health:</strong> Implants stimulate the jawbone and
                help preserve its structure, while dentures can contribute to
                bone loss over time.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Longevity:</strong> Implants tend to last longer than
                dentures, which may need to be replaced every 5-10 years due to
                wear and tear.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Advantages of Dental Implants
            </Heading>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Long-Lasting:</strong> Implants can last a lifetime with
                proper care, making them a cost-effective long-term solution.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Improved Appearance:</strong> They look and feel like
                your natural teeth, providing a seamless and aesthetically
                pleasing smile.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Better Functionality:</strong> Implants restore full
                chewing function, making it easier to eat and enjoy a variety of
                foods.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>No Damage to Adjacent Teeth:</strong> Unlike bridges,
                which require altering neighboring teeth, implants don’t affect
                the surrounding healthy teeth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Enhanced Confidence:</strong> With implants, you can
                smile, speak, and eat with confidence, without worrying about
                your teeth shifting or falling out.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dental Implant Procedure
            </Heading>
            <Text mt={2} fontWeight="light">
              The dental implant process typically involves several stages:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Consultation and Planning:</strong> Your dentist or oral
                surgeon will conduct a thorough examination, which may include
                X-rays or CT scans, to assess your oral health and determine if
                you're a good candidate for implants.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Implant Placement:</strong> The implant post is
                surgically placed into the jawbone. This step may take a few
                hours, and you may be given a local anesthetic or sedation.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Healing and Osseointegration:</strong> After implant
                placement, you’ll need to wait for the bone to fuse with the
                implant in a process called osseointegration. This can take
                anywhere from a few months to half a year, depending on the
                individual.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Placement of the Abutment:</strong> Once
                osseointegration is complete, a small connector (abutment) is
                attached to the implant. This abutment will hold the replacement
                tooth or crown in place.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Attachment of the Replacement Tooth:</strong> Finally, a
                custom-made crown, bridge, or denture is attached to the
                abutment, completing the implant process.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Post-Operative Instructions
            </Heading>
            <Text mt={2} fontWeight="light">
              After your dental implant procedure, you’ll need to follow certain
              instructions to ensure proper healing:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Pain Management:</strong> You may experience mild
                discomfort or swelling, which can be managed with
                over-the-counter pain medications.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Diet:</strong> Stick to soft foods for the first few
                days following surgery. Avoid hot foods and drinks as they can
                cause discomfort.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Oral Hygiene:</strong> Gently clean the surgical site
                with a soft toothbrush and avoid brushing the area directly
                after the procedure until the area heals.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Avoid Smoking:</strong> Smoking can interfere with
                healing and may increase the risk of complications.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Post-Implants Care
            </Heading>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Regular Checkups:</strong> Visit your dentist for
                regular follow-up appointments to ensure the implants are
                healing properly and to monitor the condition of the implant.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Oral Hygiene:</strong> Maintain good oral hygiene by
                brushing and flossing regularly to keep your implants clean and
                prevent infection.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Avoid Hard Foods:</strong> Until your implants are fully
                integrated with the bone, avoid hard or chewy foods that could
                damage the implant or your new teeth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Protect the Implants:</strong> If you grind your teeth,
                talk to your dentist about wearing a nightguard to protect your
                implants from excessive pressure.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default DentalImplants;
